import { Component, createContext } from 'react'
import { withRouter } from 'next/router'

//* HOCs
import withUserContext from '../consumerHOC/UserConsumer'

//* Helpers
import { api, setCookie, getCookie, removeCookie, asyncFunctionDecorator } from 'helpers'


const CartContext = createContext(null)
export const CartConsumer = CartContext.Consumer

class CartProvider extends Component {

    state = {
        itemsCount: 0,
        items: [],
        subtotal: 0,
        shippingCoast: '-',
        total: 0,
        cartLoading: null,
    }

    methods = {
        addToCart: this.addToCart.bind(this),
        getCart: this.getCart.bind(this),
        removeCart: this.removeCart.bind(this),
        getShippingCoast: this.getShippingCoast.bind(this),
        checkout: this.checkout.bind(this),
        clearCart: this.clearCart.bind(this)
    }

    componentDidMount() {
        this.getCart()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.isLoggedIn !== prevProps.isLoggedIn || (prevProps.router.locale !== this.props.router.locale) || (this.props.router.pathname !== prevProps.router.pathname)) {
            this.getCart()
        }
    }

    enableCartLoading() {
        this.setState({ cartLoading: true })
    }

    async getCart() {
        this.enableCartLoading()
        if (this.props.isLoggedIn) {
            return await api.get('cart', this.props.router.locale).then(({ data }) => this.setResourceDecorator(data))
        } else {
            const cookieCart = getCookie('cart')
            const cart = cookieCart ? JSON.parse(cookieCart) : null

            if (cart) {
                return await api.post('guest-cart', { cart }, this.props.router.locale,).then(({ data }) => this.setResourceDecorator(data))
            } else {
                this.clearCart();
            }
        }
    }

    addToCart({ productId, variantId, optionId, qty, update = false }) {
        if (this.props.isLoggedIn) {
            return this.customerAddToCart({ productId, variantId, optionId, qty })
        } else {
            return this.guestAddToCart({ productId, variantId, optionId, qty, update })
        }
    }

    guestAddToCart({ productId, variantId, optionId, qty, update }) {
        const existingItem = this.state.items.find(item => item.productId === productId && item.variantId === variantId && item.optionId === optionId)

        let items = []

        if (!existingItem) {
            items = this.state.items.concat({ productId, variantId, optionId, qty })
        }
        else {
            items = this.state.items.map(item => (item.productId === productId && item.variantId === variantId && item.optionId === optionId) ?
                Object.assign({}, item, { productId, variantId, optionId, qty, product: { ...item.product } }) : item
            )
        }

        this.setState({ items }, () => {
            setCookie('cart', JSON.stringify(this.state.items))
            update && this.getCart()
        })

        return Promise.resolve()
    }

    async customerAddToCart({ productId, variantId, optionId, qty }) {
        return await api
            .post('add-to-cart', { productId, variantId, optionId, qty }, this.props.router.locale)
            .then(res => this.setResourceDecorator(res.data))
    }

    setResourceDecorator(data) {
        this.setState({
            itemsCount: data.items_qty,
            subtotal: data.subtotal,
            items: data.items,
            shippingCoast: data.shipping_coast,
            total: !isNaN(parseInt(data.shipping_coast)) ? data.total + data.shipping_coast : data.total,
            cartLoading: false
        })
    }

    getShippingCoast(data) {
        this.enableCartLoading()
        return asyncFunctionDecorator(async () => await api.post('shipment-cost', data, this.props.router.locale).then(res => this.setState({
            shippingCoast: res.data,
            total: this.state.subtotal + res.data,
            cartLoading: false
        })).catch((err) => {
            this.setState({ cartLoading: false })
            return Promise.reject(err)
        })
        )
    }

    checkout(data) {
        this.enableCartLoading()

        let checkoutData = {
            ...data,
            items: this.state.items,
            subtotal: this.state.subtotal,
            total: this.state.total
        }


        if (this.props.isLoggedIn) {
            checkoutData['user_id'] = this.props.user.id
        } else {
            checkoutData['user_id'] = null
        }

        return asyncFunctionDecorator(() =>
            api.post('checkout', checkoutData, this.props.router.locale).then((res) => {
                this.setState({ cartLoading: false })

                return res
            }).catch(() => {
                this.setState({ cartLoading: false })
            })
        )
    }

    async removeCart({ productId, variantId, optionId }) {
        if (this.props.isLoggedIn) {
            return await api
                .post('delete-cart-item', { productId, variantId, optionId }, this.props.router.locale)
                .then(({ data }) => this.setResourceDecorator(data))
        } else {
            const items = [...this.state.items]
            const index = items.findIndex(item => item.productId === productId && item.variantId === variantId && item.optionId === optionId)
            items.splice(index, 1)
            this.setState({ items }, () => {
                setCookie('cart', JSON.stringify(this.state.items))
                this.getCart()
            })
        }
    }

    clearCart() {
        this.setState({
            itemsCount: 0,
            subtotal: 0,
            total: 0,
            items: [],
            shippingCoast: '-',
            cartLoading: false
        })

        removeCookie('cart')
    }

    render() {
        return (
            <CartContext.Provider value={{ ...this.state, ...this.methods }}>
                {this.props.children}
            </CartContext.Provider>
        )
    }
}

export default withRouter(withUserContext(CartProvider, ['isLoggedIn', 'user']))